export const DEFAULT_FETCH_TIMEOUT_MS = 30000;

const createAbortError = (message: string) => {
	if (typeof globalThis.DOMException === 'function') {
		return new DOMException(message, 'AbortError');
	}
	const error = new Error(message);
	error.name = 'AbortError';
	return error;
};

export class FetchTimeoutError extends Error {
	constructor(timeoutMs: number) {
		super(`Request timed out after ${timeoutMs}ms`);
		this.name = 'FetchTimeoutError';
	}
}

export type FetchWithTimeoutOptions = RequestInit & {
	timeoutMs?: number;
};

type TimeoutHandle = ReturnType<typeof setTimeout>;

const clearTimer = (handle: TimeoutHandle | undefined) => {
	if (handle !== undefined) {
		clearTimeout(handle);
	}
};

export const createTimeoutController = (
	timeoutMs: number = DEFAULT_FETCH_TIMEOUT_MS,
	externalSignal?: AbortSignal | null,
) => {
	const controller = new AbortController();
	let timeout: TimeoutHandle | undefined;

	const onExternalAbort = () => {
		if (!controller.signal.aborted) {
			controller.abort(externalSignal?.reason ?? createAbortError('Aborted'));
		}
	};

	if (externalSignal) {
		if (externalSignal.aborted) {
			onExternalAbort();
		} else if (typeof externalSignal.addEventListener === 'function') {
			externalSignal.addEventListener('abort', onExternalAbort);
		}
	}

	timeout = setTimeout(() => {
		if (!controller.signal.aborted) {
			controller.abort(new FetchTimeoutError(timeoutMs));
		}
	}, timeoutMs);

	const cleanup = () => {
		clearTimer(timeout);
		timeout = undefined;
		if (externalSignal && typeof externalSignal.removeEventListener === 'function') {
			externalSignal.removeEventListener('abort', onExternalAbort);
		}
	};

	return {
		controller,
		cleanup,
	};
};

export const fetchWithTimeout = async (
	input: RequestInfo | URL,
	init: FetchWithTimeoutOptions = {},
) => {
	const { timeoutMs = DEFAULT_FETCH_TIMEOUT_MS, signal, ...rest } = init;
	const { controller, cleanup } = createTimeoutController(timeoutMs, signal ?? null);

	try {
		return await fetch(input, {
			...rest,
			signal: controller.signal,
		});
	} finally {
		cleanup();
	}
};
